import { Row } from 'antd';
import AlertsContainer from 'common/alerts/containers/alert-container';
import PermissionCheck from 'common/features/permission-check';
import KPI from 'common/kpi/kpi';
import ActionButtons from 'common/navigation/components/action-buttons';
import Notes from 'common/notes/notes';
import PublishSteps from 'common/publish-steps/containers/publish-steps-container';
import ShortTermTimeline from 'common/short-term-timeline/short-term-timeline';
import withLoading from 'common/spinner/components/with-loading';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import { Button, Col, Div } from 'ui';
import { withRouter } from 'utils/withRouter';
import { referenceTypes } from '../../../utils/app-configs';
import ContractsTable from './contracts-table';
import EquipmentsTable from './equipments-table';
import FunctionalLocationsTable from './functional-locations-table';
import InstalledAtsTable from './installed-ats-table';
import ServicesTable from './services-table';

const DashboardWrapper = styled.div`
  padding: 15px 5px;
`;

class Dashboard extends Component {
  static propTypes = {
    notes: PropTypes.array,
    kpis: PropTypes.array,
    shortTermTimeline: PropTypes.object,
    hasContract: PropTypes.bool,
    hasEquipments: PropTypes.bool,
    hasServices: PropTypes.bool,
    hasNotes: PropTypes.bool,
    hasAlert: PropTypes.bool,
    hasKpis: PropTypes.bool,
    hasInfoPane: PropTypes.bool,
    hasInstalledAts: PropTypes.bool,
    hasShortTimeline: PropTypes.bool,
    equipmentsTableData: PropTypes.array,
    equipmentCount: PropTypes.number,
    data: PropTypes.object,
    infoKey: PropTypes.string,
    activeContext: PropTypes.string,
    type: PropTypes.string,
    InfoPane: PropTypes.func,
    installedAtsTableData: PropTypes.array,
    servicesTableData: PropTypes.array,
    contactsTableData: PropTypes.array,
    activeChilds: PropTypes.array,
    activeParents: PropTypes.array,
    activeSelf: PropTypes.object,
    saveNotes: PropTypes.func,
    isNotesLoading: PropTypes.bool,
    notesError: PropTypes.object,
    id: PropTypes.string,
    fetchNotes: PropTypes.func,
    dashboardSuccess: PropTypes.func,
    renderMenu: PropTypes.func,
    canCreateOrder: PropTypes.bool,
    canCreateTicket: PropTypes.bool,
    history: PropTypes.object,
    functionalLocationsTableData: PropTypes.array,
    hasFunctionalLocations: PropTypes.bool,
    renderConnectionStatus: PropTypes.func,
  };

  getActiveIdByType = (type) => {
    const { activeChilds, activeParents, activeSelf } = this.props;
    const allNavigationItems = [...activeChilds, ...activeParents, activeSelf];
    const filtered = allNavigationItems && allNavigationItems?.filter(f => f.type === type);
    if (filtered && filtered.length) {
      return filtered[0].id;
    }
    return null;
  };

  renderKpis = () => {
    const { hasKpis = true, kpis } = this.props;

    if (hasKpis) {
      return (
        <Div marginBottom={20} data-test-id='dashboard.kpi'>
          <KPI kpis={kpis} />
        </Div>
      );
    }
    return null;
  };

  renderAlerts = () => {
    const { hasAlert = true } = this.props;

    if (hasAlert) {
      return (
        <Div marginBottom={20} data-test-id='dashboard.alerts'>
          <AlertsContainer largeTag={true} />
        </Div>
      );
    }
    return null;
  };

  renderNotes = () => {
    const { hasNotes = true, notes, type, saveNotes, isNotesLoading, notesError } = this.props;

    if (hasNotes) {
      return (
        <Div marginBottom={20} data-test-id='dashboard.notes'>
          <Notes
            notes={notes}
            type={type}
            save={saveNotes}
            loading={isNotesLoading}
            error={notesError}
          />
        </Div>
      );
    }
    return null;
  };

  renderContract = () => {
    const { contactsTableData, hasContract = false } = this.props;

    if (hasContract) {
      return <ContractsTable contracts={contactsTableData} />;
    }
    return null;
  };

  renderEquipments = () => {
    const { equipmentsTableData, hasEquipments = false } = this.props;

    if (hasEquipments) {
      const activeRowId = this.getActiveIdByType(referenceTypes.equipment);
      return (
        <Div marginBottom={20} data-test-id='dashboard.equipments'>
          <EquipmentsTable
            equipments={equipmentsTableData}
            activeRow={{ rowField: 'id', id: activeRowId }}
          />
        </Div>
      );
    }
    return null;
  };

  renderFunctionalLocations = () => {
    const { functionalLocationsTableData, hasFunctionalLocations = false } = this.props;

    if (hasFunctionalLocations) {
      const activeRowId = this.getActiveIdByType('functionalLocations');
      return (
        <Div marginBottom={20} data-test-id='dashboard.functionalLocations'>
          <FunctionalLocationsTable
            functionalLocations={functionalLocationsTableData}
            activeRow={{ rowField: 'id', id: activeRowId }}
          />
        </Div>
      );
    }
    return null;
  };

  renderServices = () => {
    const { hasServices = false, servicesTableData, activeContext } = this.props;

    if (hasServices && servicesTableData?.length) {
      return (
        <Div marginBottom={20} data-test-id='dashboard.services'>
          <ServicesTable
            services={servicesTableData}
            activeRow={{ rowField: 'id', id: activeContext }}
          />
        </Div>
      );
    }
    return null;
  };

  renderInfoPane = () => {
    const { hasInfoPane = false, InfoPane = null, infoKey, data } = this.props;

    if (hasInfoPane && data[infoKey]) {
      return (
        <Div marginBottom={20} data-test-id='dashboard.info'>
          <InfoPane {...data[infoKey]} />
        </Div>
      );
    }
    return null;
  };

  renderInstalledAts = () => {
    const {
      installedAtsTableData,
      hasInstalledAts = false,
      equipmentCount,
    } = this.props;

    if (hasInstalledAts) {
      const activeRowId = this.getActiveIdByType('installedat');
      return (
        <Div marginBottom={20} data-test-id='dashboard.installedats'>
          <InstalledAtsTable
            installedAts={installedAtsTableData}
            equipmentCount={equipmentCount}
            activeRow={{ rowField: 'id', id: activeRowId }}
          />
        </Div>
      );
    }
    return null;
  };

  renderShortTimeline = () => {
    const { shortTermTimeline, hasShortTimeline = false } = this.props;

    if (hasShortTimeline) {
      return (
        <Div marginBottom={20}>
          <ShortTermTimeline segments={shortTermTimeline} />
        </Div>
      );
    }
    return null;
  };

  render() {
    const {
      id,
      fetchNotes,
      type,
      dashboardSuccess,
      renderMenu,
      data,
      canCreateOrder,
      canCreateTicket,
      history,
      renderConnectionStatus,
    } = this.props;

    return (
      <>
        {renderMenu(
          <ActionButtons>
            {renderConnectionStatus && renderConnectionStatus()}
            {data?.isInactive || !canCreateOrder ?
              null :
              <PermissionCheck permissionsList={['ordercreate']}>
                <Button
                  data-test-id='create-ad-hoc-order-button'
                  type='secondary'
                  onClick={() => history.push(`create-order/${type}/${id}`)}
                >
                  <FormattedMessage id='common.CREATE_ORDER' />
                </Button>
              </PermissionCheck>
            }
            {canCreateTicket ? <TicketButton id={id} /> : null}
          </ActionButtons>,
        )}
        <PublishSteps
          id={id}
          reloadData={() => fetchNotes({ id, type })}
          onDataReloaded={dashboardSuccess}
          type={type}
        />
        <DashboardWrapper data-test-id='dashboard'>
          <Row gutter={10}>
            <Col md={18} sm={24} marginRight={0} marginLeft={0}>
              <Row gutter={10}>
                <Col md={16}>{this.renderKpis()}</Col>
                <Col md={8}>{this.renderAlerts()}</Col>
              </Row>
              <Row>
                <Col md={24}>{this.renderShortTimeline()}</Col>
              </Row>
            </Col>
            <Col md={6} sm={24}>
              {this.renderNotes()}
            </Col>
          </Row>
          {this.renderInfoPane()}
          {this.renderFunctionalLocations()}
          {this.renderEquipments()}
          {this.renderServices()}
          {this.renderContract()}
          {this.renderInstalledAts()}
        </DashboardWrapper>
      </>
    );
  }
}

export default withLoading(withRouter(Dashboard), 'common.LOADING_DASHBOARD', true);
